/**
 * Kick-off "Kickoff Data" AI-enrichment FRONT-END model (pure, DOM-free).
 *
 * 1:1 port of the non-render logic of `../EER app/public/assets/js/project.js`:
 *   - `renderKickoffPanel` (~1540-1589) — the collapsible panel with the seven
 *     clickable sections (Summary / Company / Position / Requirements /
 *     Compensation / Search-Sourcing / Missing data), each a one-line preview;
 *   - `openKickoffDetail` (~1613-1662) — the per-section detail dialog with the
 *     legacy sub-fields (Company: name/industry/headquarters/employees/founded/
 *     website/description; Position: title/location/remote/type/department/
 *     reports-to/team-size; Requirements: experience/education/must-have/
 *     nice-to-have/languages; Compensation: salary/bonus/benefits/equity;
 *     Sourcing: keywords/target-industries/target-companies/DNS-companies).
 *
 * The enrichment JSON lives on `projects.data.kickoff_enrichment` (persisted form
 * of `@eer/shared` `KickoffEnrichmentResult`, contracts/kickoff-enrichment.ts).
 * Every section is `Record<string, unknown> | null` (the model may omit a section)
 * so every read here is defensive — a garbled / partial enrichment degrades to an
 * empty section instead of throwing. Kept framework-free so `KickoffDataPanel.tsx`
 * stays a thin shell and the unit tests exercise the mapping with no DOM.
 */

/** The seven section keys, in the legacy render order (`renderKickoffPanel`). */
export const KICKOFF_ENRICHMENT_SECTION_KEYS = [
  'summary',
  'company_profile',
  'position',
  'requirements',
  'compensation',
  'sourcing',
  'missing_info',
] as const;
export type KickoffEnrichmentSectionKey = (typeof KICKOFF_ENRICHMENT_SECTION_KEYS)[number];

/** Human labels for each section (legacy `openKickoffDetail` titles + panel bold text). */
export const KICKOFF_ENRICHMENT_SECTION_TITLES: Record<KickoffEnrichmentSectionKey, string> = {
  summary: 'Summary',
  company_profile: 'Company Profile',
  position: 'Position',
  requirements: 'Requirements',
  compensation: 'Compensation',
  sourcing: 'Search / Sourcing',
  missing_info: 'Missing Information',
};

// ---------------------------------------------------------------------------
// Defensive readers (the enrichment bag is Record<string, unknown> | null)
// ---------------------------------------------------------------------------

/** A plain object or `null` — every enrichment section is one of these. */
function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/** A trimmed string from any scalar (number/string), else '' (never throws). */
function str(v: unknown): string {
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return '';
}

/** A string array from a `string[]` (dropping blanks), else []. */
function strList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map(str).filter((s) => s.length > 0);
}

/** A boolean flag (only literal `true` counts — legacy `pos.is_new === true`). */
function isTrue(v: unknown): boolean {
  return v === true;
}

// ---------------------------------------------------------------------------
// Panel preview rows (renderKickoffPanel — the clickable one-liners)
// ---------------------------------------------------------------------------

/** One clickable panel row: the section key, its bold label, a one-line preview. */
export interface KickoffEnrichmentPreview {
  readonly key: KickoffEnrichmentSectionKey;
  /** The bold prefix shown in the panel (Summary / Company / Position / …). */
  readonly label: string;
  /** The one-line preview text (already truncated / joined). */
  readonly preview: string;
  /** Missing-data count (only set for the `missing_info` row). */
  readonly count?: number;
}

/** Truncate to `n` chars with an ellipsis (legacy panel `s.substring(0,n)+'...'`). */
export function truncate(s: string, n: number): string {
  return s.length > n ? `${s.substring(0, n)}…` : s;
}

/** The parsed enrichment summary (union `string | { … }` → a display string). */
function readSummary(ke: Record<string, unknown>): string {
  const s = ke['summary'];
  if (typeof s === 'string') return s.trim();
  // The contract allows an object summary; surface a stringified brief when so.
  const rec = asRecord(s);
  if (rec) {
    const text = str(rec['text']) || str(rec['overview']);
    if (text) return text;
  }
  return '';
}

/**
 * Build the clickable preview rows for the panel (`renderKickoffPanel`). Only the
 * sections that carry data appear (legacy dropped empty sections), in the fixed
 * legacy order. Returns `[]` when the enrichment is absent / entirely empty (the
 * panel then renders nothing — legacy `if (!ke) return ''` + `if (!sections.length)`).
 */
export function buildKickoffPreviews(
  enrichment: Record<string, unknown> | null | undefined,
): readonly KickoffEnrichmentPreview[] {
  const ke = asRecord(enrichment);
  if (!ke) return [];
  const rows: KickoffEnrichmentPreview[] = [];

  const summary = readSummary(ke);
  if (summary) {
    rows.push({ key: 'summary', label: 'Summary', preview: truncate(summary, 120) });
  }

  const cp = asRecord(ke['company_profile']);
  if (cp) {
    const items = [
      str(cp['name']),
      str(cp['industry']),
      str(cp['headquarters']),
      str(cp['employees']) ? `${str(cp['employees'])} emp.` : '',
    ].filter((x) => x.length > 0);
    if (items.length) {
      rows.push({ key: 'company_profile', label: 'Company', preview: items.join(' · ') });
    }
  }

  const pos = asRecord(ke['position']);
  if (pos) {
    const items = [
      str(pos['title']),
      str(pos['location']),
      str(pos['remote_policy']),
      isTrue(pos['is_new']) ? 'New position' : isTrue(pos['is_replacement']) ? 'Replacement' : '',
    ].filter((x) => x.length > 0);
    if (items.length) {
      rows.push({ key: 'position', label: 'Position', preview: items.join(' · ') });
    }
  }

  const req = asRecord(ke['requirements']);
  if (req) {
    const parts: string[] = [];
    const exp = str(req['experience_years']);
    if (exp) parts.push(`Experience: ${exp}`);
    const must = strList(req['must_have']);
    if (must.length) {
      parts.push(`Must: ${must.slice(0, 3).join(', ')}${must.length > 3 ? '…' : ''}`);
    }
    if (parts.length) {
      rows.push({ key: 'requirements', label: 'Requirements', preview: parts.join(' · ') });
    }
  }

  const comp = asRecord(ke['compensation']);
  if (comp) {
    const salary = str(comp['salary_range']);
    const currency = str(comp['currency']);
    const items = [
      salary ? `${salary}${currency ? ` ${currency}` : ''}` : '',
      str(comp['bonus']),
      str(comp['benefits']),
    ].filter((x) => x.length > 0);
    if (items.length) {
      rows.push({ key: 'compensation', label: 'Compensation', preview: items.join(' · ') });
    }
  }

  const src = asRecord(ke['sourcing']);
  if (src) {
    const parts: string[] = [];
    const kw = strList(src['keywords']);
    if (kw.length) {
      parts.push(`Keywords: ${kw.slice(0, 5).join(', ')}${kw.length > 5 ? '…' : ''}`);
    }
    const tc = strList(src['target_companies']);
    if (tc.length) {
      parts.push(`Target: ${tc.slice(0, 3).join(', ')}${tc.length > 3 ? '…' : ''}`);
    }
    if (parts.length) {
      rows.push({ key: 'sourcing', label: 'Search', preview: parts.join(' · ') });
    }
  }

  const missing = strList(ke['missing_info']);
  if (missing.length) {
    rows.push({
      key: 'missing_info',
      label: `Missing data (${missing.length})`,
      preview: '',
      count: missing.length,
    });
  }

  return rows;
}

/** The relative date shown next to the panel title (`enriched_at`, en-US date). */
export function enrichedAtLabel(enrichment: Record<string, unknown> | null | undefined): string {
  const ke = asRecord(enrichment);
  if (!ke) return '';
  const raw = str(ke['enriched_at']);
  if (!raw) return '';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en');
}

// ---------------------------------------------------------------------------
// Detail dialog body (openKickoffDetail — labelled rows / lists per section)
// ---------------------------------------------------------------------------

/** One row of a detail dialog: a label + a value (paragraph) OR a bullet list. */
export interface KickoffDetailRow {
  readonly label: string;
  readonly value?: string;
  readonly items?: readonly string[];
  /** When set, `value` is a URL to render as a link (legacy Website row). */
  readonly href?: string;
}

/** A detail-dialog body: the section title + its rows (paragraph for `summary`). */
export interface KickoffDetail {
  readonly title: string;
  /** A leading free-text paragraph (used by `summary`; empty otherwise). */
  readonly paragraph?: string;
  readonly rows: readonly KickoffDetailRow[];
}

function pushRow(rows: KickoffDetailRow[], label: string, value: string): void {
  if (value) rows.push({ label, value });
}
function pushList(rows: KickoffDetailRow[], label: string, items: readonly string[]): void {
  if (items.length) rows.push({ label, items });
}

/**
 * Build the detail-dialog body for a section (`openKickoffDetail`). Mirrors the
 * legacy per-section field set exactly; empty fields are dropped (legacy `row()`
 * returned '' for a falsy value). Returns `null` when the section carries no data
 * (the dialog then shows the empty placeholder).
 */
export function buildKickoffDetail(
  enrichment: Record<string, unknown> | null | undefined,
  key: KickoffEnrichmentSectionKey,
): KickoffDetail | null {
  const ke = asRecord(enrichment);
  if (!ke) return null;
  const title = KICKOFF_ENRICHMENT_SECTION_TITLES[key];

  if (key === 'summary') {
    const summary = readSummary(ke);
    if (!summary) return null;
    return { title, paragraph: summary, rows: [] };
  }

  if (key === 'company_profile') {
    const cp = asRecord(ke['company_profile']);
    if (!cp) return null;
    const rows: KickoffDetailRow[] = [];
    pushRow(rows, 'Name', str(cp['name']));
    pushRow(rows, 'Industry', str(cp['industry']));
    pushRow(rows, 'Headquarters', str(cp['headquarters']));
    pushRow(rows, 'Employees', str(cp['employees']));
    pushRow(rows, 'Founded', str(cp['founded']));
    const website = str(cp['website']);
    if (website) rows.push({ label: 'Website', value: website, href: website });
    pushRow(rows, 'Description', str(cp['description']));
    return rows.length ? { title, rows } : null;
  }

  if (key === 'position') {
    const pos = asRecord(ke['position']);
    if (!pos) return null;
    const rows: KickoffDetailRow[] = [];
    pushRow(rows, 'Title', str(pos['title']));
    pushRow(rows, 'Location', str(pos['location']));
    pushRow(rows, 'Remote', str(pos['remote_policy']));
    const type = isTrue(pos['is_new'])
      ? 'New position'
      : isTrue(pos['is_replacement'])
        ? 'Replacement'
        : '';
    pushRow(rows, 'Type', type);
    pushRow(rows, 'Department', str(pos['department']));
    pushRow(rows, 'Reports to', str(pos['reports_to']));
    pushRow(rows, 'Team size', str(pos['team_size']));
    return rows.length ? { title, rows } : null;
  }

  if (key === 'requirements') {
    const req = asRecord(ke['requirements']);
    if (!req) return null;
    const rows: KickoffDetailRow[] = [];
    pushRow(rows, 'Experience', str(req['experience_years']));
    pushRow(rows, 'Education', str(req['education']));
    pushList(rows, 'Must have', strList(req['must_have']));
    pushList(rows, 'Nice to have', strList(req['nice_to_have']));
    const languages = strList(req['languages']);
    if (languages.length) rows.push({ label: 'Languages', value: languages.join(', ') });
    return rows.length ? { title, rows } : null;
  }

  if (key === 'compensation') {
    const comp = asRecord(ke['compensation']);
    if (!comp) return null;
    const rows: KickoffDetailRow[] = [];
    const salary = str(comp['salary_range']);
    const currency = str(comp['currency']);
    pushRow(rows, 'Salary', salary ? `${salary}${currency ? ` ${currency}` : ''}` : '');
    pushRow(rows, 'Bonus', str(comp['bonus']));
    pushRow(rows, 'Benefits', str(comp['benefits']));
    pushRow(rows, 'Equity', str(comp['equity']));
    return rows.length ? { title, rows } : null;
  }

  if (key === 'sourcing') {
    const src = asRecord(ke['sourcing']);
    if (!src) return null;
    const rows: KickoffDetailRow[] = [];
    const keywords = strList(src['keywords']);
    if (keywords.length) rows.push({ label: 'Keywords', value: keywords.join(', ') });
    const industries = strList(src['target_industries']);
    if (industries.length) rows.push({ label: 'Target industries', value: industries.join(', ') });
    const companies = strList(src['target_companies']);
    if (companies.length) rows.push({ label: 'Target companies', value: companies.join(', ') });
    const dns = strList(src['dns_companies']);
    if (dns.length) rows.push({ label: 'DNS companies', value: dns.join(', ') });
    return rows.length ? { title, rows } : null;
  }

  // missing_info — a bullet list of the gaps.
  const missing = strList(ke['missing_info']);
  if (!missing.length) return null;
  return { title, rows: [{ label: '', items: missing }] };
}

// ---------------------------------------------------------------------------
// Transcript Summary (project-kickoff.js ~195-198)
// ---------------------------------------------------------------------------

/**
 * Read `projects.data.kickoff_transcript_summary` (legacy `kd.transcript_summary`,
 * project-kickoff.js ~195). Returns '' when absent — the Transcript Summary block
 * is then hidden. Defensive: the data bag is `Record<string, unknown>`.
 */
export function readTranscriptSummary(data: Record<string, unknown> | null | undefined): string {
  if (!data || typeof data !== 'object') return '';
  const raw =
    (data as { kickoff_transcript_summary?: unknown }).kickoff_transcript_summary ??
    (data as { transcript_summary?: unknown }).transcript_summary;
  return typeof raw === 'string' ? raw.trim() : '';
}

/**
 * Read `projects.data.kickoff_enrichment` (the enrichment bag `KickoffDataPanel`
 * renders). Returns `null` when absent / not an object. Defensive.
 */
export function readKickoffEnrichment(
  data: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!data || typeof data !== 'object') return null;
  return asRecord((data as { kickoff_enrichment?: unknown }).kickoff_enrichment);
}
